import { useState, useEffect, useCallback } from "react";
import { db, type FocusEntry } from "@/lib/db";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getTodayString(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// Supabase sync helpers
// ---------------------------------------------------------------------------

interface SbFocusRow {
  id: string;
  user_id: string;
  text: string;
  duration_days: number;
  start_date: string;
  end_date: string;
  virtue_ids: number[] | null;
  created_at: string;
  completed: boolean;
}

function toLocal(row: SbFocusRow): FocusEntry {
  return {
    id: row.id,
    userId: row.user_id,
    text: row.text,
    durationDays: row.duration_days,
    startDate: row.start_date,
    endDate: row.end_date,
    virtueIds: row.virtue_ids ?? [],
    createdAt: row.created_at,
    completed: row.completed,
  };
}

function toRemote(e: FocusEntry): Omit<SbFocusRow, never> {
  return {
    id: e.id,
    user_id: e.userId,
    text: e.text,
    duration_days: e.durationDays,
    start_date: e.startDate,
    end_date: e.endDate,
    virtue_ids: e.virtueIds.length > 0 ? e.virtueIds : null,
    created_at: e.createdAt,
    completed: e.completed,
  };
}

// ---------------------------------------------------------------------------
// useFocus Hook
// ---------------------------------------------------------------------------

export function useFocus() {
  const { user } = useAuth();
  const [focusEntries, setFocusEntries] = useState<FocusEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const today = getTodayString();

  // Active = not completed AND end_date >= today
  const activeFocus = focusEntries.filter(
    (f) => !f.completed && f.endDate >= today
  );

  // ---------------------------------------------------------------------------
  // Load
  // ---------------------------------------------------------------------------

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    // 1. Load local
    const local = await db.focusEntries
      .where("userId")
      .equals(user.id)
      .toArray();

    // 2. Pull from Supabase and merge
    try {
      const { data } = await supabase
        .from("focus_entries")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (data) {
        const remote = data.map(toLocal);
        // Merge: remote wins for existing IDs, add any missing locally
        const merged = [...local];
        for (const r of remote) {
          const idx = merged.findIndex((l) => l.id === r.id);
          if (idx === -1) {
            merged.push(r);
            await db.focusEntries.put(r);
          } else {
            merged[idx] = r;
            await db.focusEntries.put(r);
          }
        }
        setFocusEntries(merged.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
        setLoading(false);
        return;
      }
    } catch {
      // Supabase table may not exist yet — use local only
    }

    setFocusEntries(local.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
    setLoading(false);
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  // ---------------------------------------------------------------------------
  // Add
  // ---------------------------------------------------------------------------

  const addFocus = useCallback(
    async (params: {
      text: string;
      durationDays: number;
      virtueIds: number[];
    }) => {
      if (!user) return;
      const startDate = getTodayString();
      const entry: FocusEntry = {
        id: crypto.randomUUID(),
        userId: user.id,
        text: params.text.trim(),
        durationDays: params.durationDays,
        startDate,
        endDate: addDays(startDate, params.durationDays),
        virtueIds: params.virtueIds,
        createdAt: new Date().toISOString(),
        completed: false,
      };

      await db.focusEntries.add(entry);
      try {
        await supabase.from("focus_entries").insert(toRemote(entry));
      } catch { /* ignore if table not available */ }

      setFocusEntries((prev) => [entry, ...prev]);
    },
    [user]
  );

  // ---------------------------------------------------------------------------
  // Complete / Delete
  // ---------------------------------------------------------------------------

  const completeFocus = useCallback(async (id: string) => {
    await db.focusEntries.update(id, { completed: true });
    try {
      await supabase.from("focus_entries").update({ completed: true }).eq("id", id);
    } catch { /* ignore */ }
    setFocusEntries((prev) =>
      prev.map((f) => (f.id === id ? { ...f, completed: true } : f))
    );
  }, []);

  const deleteFocus = useCallback(async (id: string) => {
    await db.focusEntries.delete(id);
    try {
      await supabase.from("focus_entries").delete().eq("id", id);
    } catch { /* ignore */ }
    setFocusEntries((prev) => prev.filter((f) => f.id !== id));
  }, []);

  return {
    focusEntries,
    activeFocus,
    loading,
    addFocus,
    completeFocus,
    deleteFocus,
    reload: load,
  };
}

// ---------------------------------------------------------------------------
// Rotation helpers (used by HomePage)
// ---------------------------------------------------------------------------

/**
 * Returns a stable rotation index for a given date + slot combination.
 * Changes every new (date × slot) pair.
 */
export function getRotationIndex(date: string, slot: number): number {
  const d = new Date(date + "T00:00:00");
  const epoch = new Date("2024-01-01T00:00:00");
  const daysSince = Math.floor((d.getTime() - epoch.getTime()) / 86400000);
  return daysSince * 6 + (slot - 1);
}

/**
 * Returns N items from an array starting at a stable rotation offset,
 * wrapping around if needed.
 */
export function rotateSlice<T>(arr: T[], count: number, index: number): T[] {
  if (arr.length === 0 || count === 0) return [];
  const n = Math.min(count, arr.length);
  const start = index % arr.length;
  const result: T[] = [];
  for (let i = 0; i < n; i++) {
    result.push(arr[(start + i) % arr.length]);
  }
  return result;
}
